import { Dialog, Tab, TabView, useDialog } from 'components';
import DataTable, { DataTableColumn } from 'components/tables/data-table';
import { useDamageOptions } from 'contexts/damage-options';
import { FC, useCallback, useMemo, useState } from 'react';
import { DamageOption, EquipmentType } from 'types/damage/damage-types';
import './damage-options.scss';

const containerColumns: DataTableColumn[] = [
  { field: 'damageName', label: 'Option', width: 80 },
  { field: 'subdivision1', label: 'Location', width: 80 },
  { field: 'subdivision2', label: 'Component', width: 80 }
];

const chassisColumns: DataTableColumn[] = [
  { field: 'damageName', label: 'Option', width: 80 },
  { field: 'subdivision1', label: 'System', width: 80 },
  { field: 'subdivision2', label: 'Component', width: 80 }
];

const gensetColumns: DataTableColumn[] = [{ field: 'damageName', label: 'Option', width: 80 }];

type SectionProps = {
  equipmentType: EquipmentType;
  list: DamageOption[];
  columns: DataTableColumn[];
  csvFilename: string;
};

const blankDraft = (equipmentType: EquipmentType): Partial<DamageOption> => ({
  equipmentType,
  subdivision1: '',
  subdivision2: '',
  damageName: ''
});

const DamageOptionsSection: FC<SectionProps> = ({ equipmentType, list, columns, csvFilename }) => {
  const { saveDamageOption, deleteDamageOptions } = useDamageOptions();
  const { ref, openDialog, closeDialog } = useDialog({});
  const [selectedRows, setSelectedRows] = useState<DamageOption[]>([]);
  const [draft, setDraft] = useState<Partial<DamageOption>>(() => blankDraft(equipmentType));
  /* id of the option being edited — edit = delete old + create new (no update endpoint) */
  const [editId, setEditId] = useState<number | undefined>(undefined);

  const fetchData = useCallback(() => Promise.resolve(list), [list]);

  const showSubdivisions = equipmentType === EquipmentType.CONTAINER || equipmentType === EquipmentType.CHASSIS;
  const subdivision1Label = equipmentType === EquipmentType.CONTAINER ? 'Location' : 'System';

  /* seed the draft (equipment type is fixed by the active sub-tab), then open the dialog */
  const openFor = useCallback(
    (option?: DamageOption) => {
      setEditId(option?.id);
      setDraft(
        option
          ? {
              equipmentType,
              subdivision1: option.subdivision1 ?? '',
              subdivision2: option.subdivision2 ?? '',
              damageName: option.damageName ?? ''
            }
          : blankDraft(equipmentType)
      );
      openDialog();
    },
    [equipmentType, openDialog]
  );

  const handleClose = useCallback(() => {
    setEditId(undefined);
    closeDialog();
  }, [closeDialog]);

  const handleSave = useCallback(() => {
    if (editId !== undefined) {
      deleteDamageOptions([editId]);
    }
    saveDamageOption(draft as DamageOption);
    handleClose();
  }, [draft, editId, saveDamageOption, deleteDamageOptions, handleClose]);

  return (
    <>
      <Dialog ref={ref} closeDialog={handleClose}>
        <div className="dialog-header">
          <h3>{editId !== undefined ? 'Edit Damage Option' : 'New Damage Option'}</h3>
        </div>
        <div className="dialog-body">
          {showSubdivisions && (
            <>
              <div>
                <p className="margin-top-16px">{subdivision1Label}</p>
                <input
                  type="text"
                  value={draft.subdivision1}
                  onChange={({ target }) => setDraft({ ...draft, subdivision1: target.value })}
                />
              </div>
              <div>
                <p className="margin-top-16px">Part</p>
                <input
                  type="text"
                  value={draft.subdivision2}
                  onChange={({ target }) => setDraft({ ...draft, subdivision2: target.value })}
                />
              </div>
            </>
          )}
          <div>
            <p className="margin-top-16px">Name</p>
            <input
              type="text"
              value={draft.damageName}
              onChange={({ target }) => setDraft({ ...draft, damageName: target.value })}
            />
          </div>
          <div className="d-flex justify-content-between margin-top-16px">
            <button className="btn-outline" onClick={handleClose}>
              Cancel
            </button>
            <button className="btn-primary" disabled={!draft.damageName} onClick={handleSave}>
              <span>Save</span>
            </button>
          </div>
        </div>
      </Dialog>
      <div className="d-flex justify-content-end g-3 align-items-center mb-2">
        {!!selectedRows.length && (
          <>
            <button
              className="btn-danger"
              onClick={() => deleteDamageOptions(selectedRows.filter(r => !!r.id).map(r => r.id) as number[])}
            >
              delete
            </button>
            <button
              className="btn-outline"
              disabled={selectedRows.length !== 1}
              onClick={() => openFor(selectedRows[0])}
            >
              edit
            </button>
          </>
        )}
        <button className="btn-primary" onClick={() => openFor()}>
          NEW OPTION
        </button>
      </div>
      <DataTable
        key={`${equipmentType}-${list.length}`}
        tableClass="t-height"
        fetchData={fetchData}
        onRowSelectionModelChange={setSelectedRows}
        initialPage={{ sortField: 'damageName', sortDirection: 'asc', pageSize: 25 }}
        columns={columns}
        initialColumns={columns}
        clientSidePagination
        csvFilename={csvFilename}
        hideTableControls
        checkboxSelection
      />
    </>
  );
};

export const DamageOptionsTab: FC = () => {
  const { damageOptionList } = useDamageOptions();

  const tabs: Tab[] = useMemo(
    () => [
      {
        label: 'Container',
        content: (
          <DamageOptionsSection
            equipmentType={EquipmentType.CONTAINER}
            list={damageOptionList.filter(d => d.equipmentType === EquipmentType.CONTAINER)}
            columns={containerColumns}
            csvFilename="ContainerDamageOptions.csv"
          />
        )
      },
      {
        label: 'Chassis',
        content: (
          <DamageOptionsSection
            equipmentType={EquipmentType.CHASSIS}
            list={damageOptionList.filter(d => d.equipmentType === EquipmentType.CHASSIS)}
            columns={chassisColumns}
            csvFilename="ChassisDamageOptions.csv"
          />
        )
      },
      {
        label: 'Genset',
        content: (
          <DamageOptionsSection
            equipmentType={EquipmentType.GENSET}
            list={damageOptionList.filter(d => d.equipmentType === EquipmentType.GENSET)}
            columns={gensetColumns}
            csvFilename="GensetDamageOptions.csv"
          />
        )
      }
    ],
    [damageOptionList]
  );

  return (
    <div id="damage-options">
      <TabView tabs={tabs} variant="embedded" />
    </div>
  );
};
